import { useEffect, useState } from "react";
import { Map as MapIcon, CheckCircle2, AlertCircle, Save, RefreshCw, PlugZap } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { getMapsSettings, saveMapsSettings, type MapsSettings } from "../../../../lib/mapsSettings";
import type { MapProbeResult, MapsTestReason } from "../../../../lib/googleMaps";
import { Btn, StatusPill } from "../../../ui";
import { ConnStatus, IntegrationCard, PanelLoader } from "../panel/IntegrationCard";
import { CopyPill, Field, Note, Pill, SaveRow, SecretPill } from "../panel/fields";
import { MapKeyPreview } from "./MapKeyPreview";

/**
 * Which sentence explains each failure. A lookup rather than a chain of
 * ternaries, so a reason the probe learns to report and the panel has not
 * learned to phrase yet is a missing key rather than a blank line.
 */
const FAIL_KEY: Record<MapsTestReason, string> = {
  auth: "settings.maps.testFailAuth",
  network: "settings.maps.testFailNetwork",
  script: "settings.maps.testFailScript",
};

/**
 * The referrer pattern to paste into the Google Cloud console.
 *
 * Built from the address the operator is standing on rather than from a
 * configured base URL, because the CRM answers on more than one host
 * (`crm.webadmin.ee` and the platform domain) and the restriction has to name
 * the one they actually use. `/*` covers every path; Google matches the whole
 * URL, so the bare host alone would reject every page but the root.
 */
function referrerPattern(): string {
  if (typeof window === "undefined") return "";
  return `${window.location.protocol}//${window.location.host}/*`;
}

/** Settings -> Integrations: the Google Maps key the company map draws with. */
export function MapsCard() {
  const t = useT();
  const [settings, setSettings] = useState<MapsSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [key, setKey] = useState("");
  // The Places key is never sent to the browser, so this box starts empty and
  // an empty box means "leave the stored one alone". Opposite of the browser
  // key above, which is prefilled and can therefore be cleared by emptying it.
  const [serverKey, setServerKey] = useState("");
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [test, setTest] = useState<MapProbeResult | null>(null);
  // The key the preview frame is currently drawing, and which attempt it is.
  // Held apart from the input so that typing does not spawn a map on every
  // keystroke - the frame only changes when the operator asks for it.
  const [probe, setProbe] = useState<{ key: string; attempt: number } | null>(null);

  useEffect(() => {
    getMapsSettings()
      .then((s) => {
        setSettings(s);
        setKey(s.browserKey);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaveState("saving");
    try {
      await saveMapsSettings({
        browserKey: key.trim(),
        ...(serverKey ? { serverKey: serverKey.trim() } : {}),
      });
      setServerKey("");
      setSettings(await getMapsSettings());
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 2500);
    } catch {
      setSaveState("error");
    }
  }

  /** Draws what is in the box, not what is stored - so a key can be proven
   *  before it replaces a working one. */
  function handleTest() {
    setTest(null);
    setProbe((p) => ({ key: key.trim(), attempt: (p?.attempt ?? 0) + 1 }));
  }

  if (loading) return <PanelLoader />;

  const dirty = key.trim() !== (settings?.browserKey ?? "");

  return (
    <IntegrationCard
      icon={MapIcon}
      title={t("settings.maps.title")}
      desc={t("settings.maps.desc")}
      status={
        <ConnStatus
          ok={!!settings?.configured}
          okLabel={t("settings.maps.statusConfigured")}
          offLabel={t("settings.maps.statusNotConfigured")}
        />
      }
    >
      <form onSubmit={handleSave} className="space-y-5">
        {settings?.source === "env" ? <Note tone="warn">{t("settings.maps.fromEnv")}</Note> : null}

        <Field label={t("settings.maps.browserKey")} hint={t("settings.maps.browserKeyHint")}>
          <Pill value={key} onChange={setKey} placeholder="AIza..." autoComplete="off" mono />
        </Field>

        {settings?.suspicious && !dirty ? (
          <Note tone="warn">{t("settings.maps.suspicious")}</Note>
        ) : null}

        <Note tone="info">
          <span className="block font-[650]">{t("settings.maps.setupTitle")}</span>
          <span className="mt-1 block opacity-90">{t("settings.maps.setupStep1")}</span>
          <span className="mt-1 block opacity-90">{t("settings.maps.setupStep2")}</span>
          <span className="mt-1 block opacity-90">{t("settings.maps.setupStep3")}</span>
        </Note>

        {/* The exact string the referrer restriction wants. Handing it over
            beats describing it: a restriction typed from memory is the single
            most common reason a fresh key answers RefererNotAllowedMapError. */}
        <Field label={t("settings.maps.referrer")} hint={t("settings.maps.referrerHint")}>
          <CopyPill
            value={referrerPattern()}
            copyLabel={t("settings.maps.copy")}
            copiedLabel={t("settings.maps.copied")}
          />
        </Field>

        <p className="crm-sub !mt-0 text-[12px]">{t("settings.maps.testHint")}</p>

        {/* The second key, for the Places lookups the company card makes. Kept
            below the map's own setup because it is optional: without it the map
            still draws, it just says nothing about reputation. */}
        <div className="border-t border-[var(--crm-hair)] pt-5">
          <Field
            label={t("settings.maps.serverKey")}
            hint={
              settings?.serverKeySet && !serverKey
                ? t("settings.maps.serverKeyKept")
                : t("settings.maps.serverKeyHint")
            }
          >
            <SecretPill
              value={serverKey}
              onChange={setServerKey}
              placeholder={settings?.serverKeySet ? t("settings.maps.serverKeyPlaceholder") : ""}
              reveal
              showLabel={t("settings.maps.showKey")}
              hideLabel={t("settings.maps.hideKey")}
            />
          </Field>
          {settings?.serverKeySource === "env" ? (
            <Note tone="warn" className="mt-3">
              {t("settings.maps.serverKeyFromEnv")}
            </Note>
          ) : null}
        </div>

        {/* The verdict and the map that produced it, in that order: the sentence
            is what the operator needs, the map is the proof underneath it. When
            there is no verdict the map stands alone, with the neutral line that
            tells the operator to read it themselves - see the warning in
            `lib/googleMaps.ts` for why nothing is asserted in that case. */}
        {test ? (
          <TestReport t={t} test={test} unsaved={dirty} />
        ) : probe ? (
          <Note tone="mut">{t("settings.maps.previewJudge")}</Note>
        ) : null}

        {probe ? (
          <MapKeyPreview mapKey={probe.key} attempt={probe.attempt} onResult={setTest} />
        ) : null}

        <SaveRow>
          <Btn type="submit" tone="dark" disabled={saveState === "saving"}>
            {saveState === "saving" ? (
              <RefreshCw className="size-3.5 animate-spin" />
            ) : (
              <Save className="size-3.5" />
            )}
            {saveState === "saving" ? t("settings.maps.saving") : t("settings.maps.save")}
          </Btn>
          {/* Never disabled while a preview is up: drawing the map again is
              exactly what an operator does after fixing the restriction in the
              Google console, and a button that waits for a verdict that may
              never come would lock them out of their own retry. */}
          <Btn onClick={handleTest} disabled={!key.trim()}>
            <PlugZap className="size-3.5" />
            {probe ? t("settings.maps.testAgain") : t("settings.maps.test")}
          </Btn>
          {saveState === "saved" ? <StatusPill tone="ok">{t("settings.maps.saved")}</StatusPill> : null}
          {saveState === "error" ? <StatusPill tone="lt">{t("settings.maps.saveError")}</StatusPill> : null}
        </SaveRow>
      </form>
    </IntegrationCard>
  );
}

/**
 * What the probe came back with.
 *
 * A block rather than a line beside the buttons: the useful part of a refusal
 * is which of the three setup steps was missed, and that does not fit on one
 * line next to two buttons.
 */
function TestReport({
  t,
  test,
  unsaved,
}: {
  t: (key: string, vars?: Record<string, string | number>) => string;
  test: MapProbeResult;
  unsaved: boolean;
}) {
  const Icon = test.ok ? CheckCircle2 : AlertCircle;

  return (
    <Note tone={test.ok ? "ok" : "bad"} icon={<Icon className="mt-0.5 size-4 shrink-0" />} live>
      <span className="block font-[650]">
        {test.ok ? t("settings.maps.testOk") : t(FAIL_KEY[test.reason])}
      </span>

      {test.ok ? (
        <span className="mt-1 block font-mono text-[11.5px] opacity-70">
          {t("settings.maps.testMeta", { ms: test.ms })}
        </span>
      ) : null}

      {/* Google's own wording, when the failure produced any. */}
      {!test.ok && test.detail ? (
        <span className="mt-1 block break-words opacity-90">
          {t("settings.maps.testDetail", { detail: test.detail })}
        </span>
      ) : null}

      {test.ok && unsaved ? <span className="mt-1.5 block opacity-90">{t("settings.maps.testUnsaved")}</span> : null}
    </Note>
  );
}
